/**
 * Minimal line-level diff helper for the admin Raw JSON confirm dialog.
 *
 * We deliberately avoid pulling in a diff library (e.g. `diff`, `jsdiff`) —
 * this is only used to preview the ~10-500 line changes an admin is about to
 * confirm, and the pretty rendering is handled by react-syntax-highlighter in
 * the confirm dialog. A classic Myers / LCS diff would be overkill here.
 *
 * The algorithm:
 * 1. Stringify both values with `JSON.stringify(value, null, 2)` so key order
 *    is stable and indentation is predictable.
 * 2. Compute a line-level LCS (longest common subsequence) between the two
 *    line arrays. This gives a minimal set of "context / added / removed"
 *    operations.
 * 3. Walk both sequences back-to-front building an op list.
 *
 * The LCS table costs O(N*M) space, which is fine for the sizes admins will
 * realistically edit in Raw mode.
 */

export type DiffOpType = "context" | "added" | "removed"

export interface DiffLine {
  /** 1-based line number in the "before" document, or null for added lines */
  beforeLine: number | null
  /** 1-based line number in the "after" document, or null for removed lines */
  afterLine: number | null
  /** The line's text content (no trailing newline) */
  text: string
  type: DiffOpType
}

export interface DiffSummary {
  added: number
  removed: number
  unchanged: number
}

export interface JsonDiffResult {
  lines: DiffLine[]
  summary: DiffSummary
}

/**
 * Serialize a value for diffing. Uses stable key order (via sort_keys-equivalent
 * in JS: pass a replacer that sorts keys) so that object-key reordering alone
 * doesn't show up as a diff — matches the backend's `compute_value_hash`
 * stability guarantee.
 */
function stableStringify(value: unknown): string {
  const sortKeys = (obj: unknown): unknown => {
    if (Array.isArray(obj)) return obj.map(sortKeys)
    if (obj && typeof obj === "object") {
      const sorted: Record<string, unknown> = {}
      for (const key of Object.keys(obj as Record<string, unknown>).sort()) {
        sorted[key] = sortKeys((obj as Record<string, unknown>)[key])
      }
      return sorted
    }
    return obj
  }
  return JSON.stringify(sortKeys(value), null, 2)
}

/**
 * Compute a line-level LCS-based diff between two JSON values.
 *
 * Returns the full list of diff lines (added / removed / context) in original
 * order plus a summary. If the two values are deeply equal, the summary has
 * zero added/removed and every line is context.
 */
export function diffJson(before: unknown, after: unknown): JsonDiffResult {
  const beforeLines = stableStringify(before).split("\n")
  const afterLines = stableStringify(after).split("\n")

  const n = beforeLines.length
  const m = afterLines.length

  // LCS length table. Classic O(n*m) DP.
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (beforeLines[i - 1] === afterLines[j - 1]) {
        lcs[i][j] = lcs[i - 1][j - 1] + 1
      } else {
        lcs[i][j] = Math.max(lcs[i - 1][j], lcs[i][j - 1])
      }
    }
  }

  // Walk back through the LCS table to produce an ordered op list.
  const ops: DiffLine[] = []
  let i = n
  let j = m
  while (i > 0 && j > 0) {
    if (beforeLines[i - 1] === afterLines[j - 1]) {
      ops.push({
        type: "context",
        beforeLine: i,
        afterLine: j,
        text: beforeLines[i - 1],
      })
      i--
      j--
    } else if (lcs[i - 1][j] >= lcs[i][j - 1]) {
      ops.push({
        type: "removed",
        beforeLine: i,
        afterLine: null,
        text: beforeLines[i - 1],
      })
      i--
    } else {
      ops.push({
        type: "added",
        beforeLine: null,
        afterLine: j,
        text: afterLines[j - 1],
      })
      j--
    }
  }
  while (i > 0) {
    ops.push({
      type: "removed",
      beforeLine: i,
      afterLine: null,
      text: beforeLines[i - 1],
    })
    i--
  }
  while (j > 0) {
    ops.push({
      type: "added",
      beforeLine: null,
      afterLine: j,
      text: afterLines[j - 1],
    })
    j--
  }

  ops.reverse()

  const summary: DiffSummary = { added: 0, removed: 0, unchanged: 0 }
  for (const op of ops) {
    if (op.type === "added") summary.added++
    else if (op.type === "removed") summary.removed++
    else summary.unchanged++
  }

  return { lines: ops, summary }
}

/**
 * Render a JsonDiffResult as a unified-diff text string (with +/-/space prefixes),
 * suitable for feeding into react-syntax-highlighter's `diff` language.
 */
export function renderUnifiedDiff(result: JsonDiffResult): string {
  const prefixFor = (type: DiffOpType): string => {
    if (type === "added") return "+"
    if (type === "removed") return "-"
    return " "
  }
  return result.lines.map((line) => `${prefixFor(line.type)}${line.text}`).join("\n")
}

/** True if the diff has no additions or removals (the two values are equivalent). */
export function isNoOpDiff(result: JsonDiffResult): boolean {
  return result.summary.added === 0 && result.summary.removed === 0
}
